import { FormEvent, useEffect, useMemo, useId, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { EarTag, useFormatEarTag } from "../../components/tags/EarTag";
import { Field } from '../../components/forms/Field';
import { SeasonMemberPicker, type SeasonMemberDraft } from "../../components/forms/SeasonMemberPicker";
import { FormModal } from '../../components/modals/FormModal';
import { BirthsSubnav } from '../../components/subnav/BirthsSubnav';
import { SortHeader } from '../../components/forms/SortHeader';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { apiError } from "../../lib/apiError";
import { formatDate, formatDateTime, SEX_LABELS, todayIso } from "../../lib/format";
import { passwordFieldProps, useFilterFieldProps } from "../../lib/inputPrivacy";
import { sireColorLabel } from "../../lib/species/sireColors";
import { compareValues, type SortDir, useTableControls } from "../../lib/tableControls";
import { loadWithReplica, mutateOrQueue, offlineQueuedInfo } from "../../lib/offline/helpers";
import {
  requireOfflineAdminPassword,
  storeAdminPasswordVerifier,
} from "../../lib/offline/offlineAdminPassword";
import { patchReplica } from "../../lib/offline/replica";
import {
  LAMB_IRZ_DISABLED_TOOLTIP,
  LAMB_IRZ_REGISTRATION_ENABLED,
} from "../../lib/plannedFeatures";
import type {
  Offspring,
  BirthEvent,
  BirthFormOptions,
  BirthSeason,
  BirthSeasonDetail,
  BreedingSeasonDetail,
  EligibleAnimal,
  PendingMother,
  Sex,
} from "../../types";

function lambTableValue(lamb: Offspring, key: string): unknown {
  if (key.startsWith("weight_")) {
    const index = Number(key.slice("weight_".length));
    const entry = lamb.weights?.find((weight) => weight.weight_index === index);
    return entry?.weight_kg ?? null;
  }
  switch (key) {
    case "birth_date":
      return lamb.birth_date;
    case "mother_tag":
      return lamb.mother_tag;
    case "sex":
      return lamb.sex;
    case "working_tag":
      return lamb.working_tag;
    case "official_tag":
      return lamb.official_tag;
    case "father_tag":
      return lamb.father_tag;
    default:
      return null;
  }
}

type OffspringDraft = {
  id?: number;
  sex: Sex;
  working_tag: string;
  official_tag: string;
  father_tag: string;
};

type Tool =
  | "event"
  | "quick"
  | "mothers"
  | "weight"
  | "rules"
  | "assign"
  | null;

type GroupSortKey = "mother_tag" | "birth_date";

type DeleteTarget =
  | { kind: "lamb"; lamb: Offspring }
  | { kind: "event"; eventId: number; motherTag: string };

type IrzTarget = {
  lamb: Offspring;
  register: boolean;
};

type MotherGroup = {
  motherTag: string;
  eventId: number | null;
  birthDate: string | null;
  offspring: Offspring[];
};

const emptyLamb = (fatherTag = ""): OffspringDraft => ({
  sex: "female",
  working_tag: "",
  official_tag: "",
  father_tag: fatherTag,
});

function normalizeTag(value: string): string {
  return value.trim();
}

function passwordOrNull(value: string): string | null {
  return value.trim() || null;
}

export function BirthsPage() {
  const formatTag = useFormatEarTag();
  const [searchParams, setSearchParams] = useSearchParams();
  const [seasons, setSeasons] = useState<BirthSeason[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<BirthSeasonDetail | null>(null);
  const [options, setOptions] = useState<BirthFormOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fieldNs = useId().replace(/:/g, "");
  const motherFilterProps = useFilterFieldProps("mother_tag");
  const workingFilterProps = useFilterFieldProps("working_tag");
  const [showMembers, setShowMembers] = useState(false);
  const [eligible, setEligible] = useState<EligibleAnimal[]>([]);
  const [rosterMembers, setRosterMembers] = useState<SeasonMemberDraft[]>([]);
  const [tool, setTool] = useState<Tool>(null);
  const [eventForm, setEventForm] = useState({
    mother_tag: "",
    birth_date: todayIso(),
    password: "",
  });
  const [lambs, setLambs] = useState<OffspringDraft[]>([emptyLamb()]);
  const [editingEventId, setEditingEventId] = useState<number | null>(null);
  const [skipPassword, setSkipPassword] = useState(false);
  const [notesLamb, setNotesLamb] = useState<Offspring | null>(null);
  const [noteText, setNoteText] = useState("");
  const [notePassword, setNotePassword] = useState("");
  const [weightsLamb, setWeightsLamb] = useState<Offspring | null>(null);
  const [weightsValues, setWeightsValues] = useState<string[]>([]);
  const [weightsPassword, setWeightsPassword] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [irzTarget, setIrzTarget] = useState<IrzTarget | null>(null);
  const [irzPassword, setIrzPassword] = useState("");
  const [groupSortKey, setGroupSortKey] = useState<GroupSortKey>("mother_tag");
  const [groupSortDir, setGroupSortDir] = useState<SortDir>("asc");
  const [withinSortKey, setWithinSortKey] = useState<string>("working_tag");
  const [withinSortDir, setWithinSortDir] = useState<SortDir>("asc");
  const [pending, setPending] = useState<PendingMother[]>([]);
  const [weightForm, setWeightForm] = useState({ working_tag: "", weight_kg: "", password: "" });
  const [assignPassword, setAssignPassword] = useState("");
  const [rules, setRules] = useState({ type: "ages" as "ages" | "interval", ages: "30, 60, 90", min: "14", every: "30" });

  async function loadPreferences() {
    const result = await api.GET("/api/birth-preferences");
    if (result.error) {
      return;
    }
    const data = result.data as { skip_birth_edit_password?: boolean };
    const skip = Boolean(data.skip_birth_edit_password);
    setSkipPassword(skip);
  }

  async function loadSeasons(preferId?: number | null) {
    const result = await loadWithReplica<BirthSeason[]>(
      "birth-seasons:active",
      () => api.GET("/api/birth-seasons", { params: { query: { scope: "active" } } })
    );
    
    if (result.error && !result.fromReplica) {
      setError(apiError(result.error, "Nie udało się wczytać wykotów."));
      return;
    }
    
    const list = result.data ?? [];
    setSeasons(list);
    const fromUrl = Number(searchParams.get("seasonId") || 0) || null;
    const explicit =
      preferId && preferId > 0 ? preferId : fromUrl && fromUrl > 0 ? fromUrl : null;
    const nextId =
      explicit ??
      (selectedId && list.some((season) => season.id === selectedId)
        ? selectedId
        : (list[0]?.id ?? null));
    setSelectedId(nextId);
    if (nextId) {
      await loadDetail(nextId);
    } else {
      setDetail(null);
    }
  }

  async function loadDetail(seasonId: number) {
    const [seasonRes, optionsRes] = await Promise.all([
      loadWithReplica<BirthSeasonDetail>(
        `birth-season-detail:${seasonId}`,
        () => api.GET("/api/birth-seasons/{season_id}", {
          params: { path: { season_id: seasonId } },
        })
      ),
      loadWithReplica<BirthFormOptions>(
        `birth-form-options:${seasonId}`,
        () => api.GET("/api/birth-form-options", {
          params: { query: { season_id: seasonId } },
        })
      ),
    ]);
    
    if (seasonRes.error && !seasonRes.fromReplica) {
      setError(apiError(seasonRes.error, "Nie udało się wczytać grupy wykotów."));
      return;
    }
    
    setError(null);
    const next = seasonRes.data;
    if (next) {
      setDetail(next);
      const schedule = next.weighing_schedule?.all_rule;
      if (schedule) {
        setRules({
          type: schedule.type,
          ages: (schedule.ages_days ?? []).join(", ") || "30, 60, 90",
          min: String(schedule.min_age_days ?? 14),
          every: String(schedule.every_days ?? 30),
        });
      }
    }
    if (optionsRes.data) {
      setOptions(optionsRes.data);
    }
  }

  async function openMembers() {
    if (!detail?.breeding_season_id) {
      setError("Ta grupa nie ma jeszcze składu — odśwież stronę i spróbuj ponownie.");
      return;
    }
    const [eligibleRes, rosterRes] = await Promise.all([
      api.GET("/api/breeding-seasons/eligible-animals", {
        params: {
          query: {
            ...(detail.breed ? { breed: detail.breed } : {}),
            ...(detail.start_date ? { as_of: detail.start_date.slice(0, 10) } : {}),
          },
        },
      }),
      api.GET("/api/breeding-seasons/{season_id}", {
        params: { path: { season_id: detail.breeding_season_id } },
      }),
    ]);
    if (eligibleRes.error) {
      setError(apiError(eligibleRes.error, "Nie udało się wczytać zwierząt do składu."));
      return;
    }
    if (rosterRes.error) {
      setError(apiError(rosterRes.error, "Nie udało się wczytać składu grupy."));
      return;
    }
    setEligible((eligibleRes.data as EligibleAnimal[]) ?? []);
    const roster = rosterRes.data as BreedingSeasonDetail;
    setRosterMembers(roster.members ?? []);
    setShowMembers(true);
  }

  async function saveMembers(members: SeasonMemberDraft[], password: string) {
    if (!detail?.breeding_season_id) {
      return;
    }
    setBusy(true);
    const updated = await api.PUT("/api/breeding-seasons/{season_id}/members", {
      params: { path: { season_id: detail.breeding_season_id } },
      body: {
        password: password || null,
        members,
      } as never,
    });
    setBusy(false);
    if (updated.error) {
      setError(apiError(updated.error, "Nie udało się zapisać składu grupy."));
      return;
    }
    setShowMembers(false);
    setError(null);
    setInfo("Zapisano skład grupy.");
    await loadDetail(detail.id);
  }

  const editMembersRequested = searchParams.get("editMembers") === "1";
  useEffect(() => {
    if (!editMembersRequested || !detail || detail.include_mating === true) {
      return;
    }
    void openMembers();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("editMembers");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMembersRequested, detail?.id]);

  useEffect(() => {
    void loadSeasons();
    void loadPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lambRows = useMemo(() => {
    const events = detail?.events ?? [];
    const byEventId = new Map(events.map((event) => [event.id, event]));
    const fromLambs = detail?.offspring ?? [];
    if (fromLambs.length) {
      return fromLambs.map((lamb) => {
        const event = byEventId.get(lamb.event_id);
        return {
          ...lamb,
          mother_tag: lamb.mother_tag || event?.mother_tag || null,
          birth_date: lamb.birth_date || event?.birth_date || null,
        };
      });
    }
    return events.flatMap((event) =>
      event.offspring.map((lamb) => ({
        ...lamb,
        mother_tag: lamb.mother_tag || event.mother_tag,
        birth_date: lamb.birth_date || event.birth_date,
      })),
    );
  }, [detail]);

  const {
    view: filteredRows,
    filters,
    setFilter,
    hasActiveFilters,
  } = useTableControls({
    rows: lambRows,
    getValue: lambTableValue,
    defaultSortKey: null,
  });

  const motherGroups = useMemo(() => {
    const groups: MotherGroup[] = [];
    const index = new Map<string, number>();
    for (const lamb of filteredRows) {
      const motherTag = lamb.mother_tag || "—";
      let group = groups[index.get(motherTag) ?? -1];
      if (!group) {
        group = {
          motherTag,
          eventId: lamb.event_id,
          birthDate: lamb.birth_date,
          offspring: [],
        };
        index.set(motherTag, groups.length);
        groups.push(group);
      }
      group.offspring.push(lamb);
      if (!group.eventId && lamb.event_id) {
        group.eventId = lamb.event_id;
      }
      if (
        lamb.birth_date &&
        (!group.birthDate || String(lamb.birth_date) < String(group.birthDate))
      ) {
        group.birthDate = lamb.birth_date;
      }
    }

    const direction = groupSortDir === "asc" ? 1 : -1;
    groups.sort((left, right) => {
      const leftValue =
        groupSortKey === "birth_date" ? left.birthDate : left.motherTag;
      const rightValue =
        groupSortKey === "birth_date" ? right.birthDate : right.motherTag;
      return compareValues(leftValue, rightValue) * direction;
    });

    const withinDirection = withinSortDir === "asc" ? 1 : -1;
    for (const group of groups) {
      group.offspring.sort(
        (left, right) =>
          compareValues(
            lambTableValue(left, withinSortKey),
            lambTableValue(right, withinSortKey),
          ) * withinDirection,
      );
    }
    return groups;
  }, [filteredRows, groupSortDir, groupSortKey, withinSortDir, withinSortKey]);

  const weightCount = detail?.weight_count ?? 0;
  const tableColSpan = 8 + weightCount;

  function toggleGroupSort(key: GroupSortKey) {
    if (groupSortKey === key) {
      setGroupSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setGroupSortKey(key);
    setGroupSortDir("asc");
  }

  function toggleWithinSort(key: string) {
    if (withinSortKey === key) {
      setWithinSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setWithinSortKey(key);
    setWithinSortDir("asc");
  }

  function closeDeleteModal() {
    setDeleteTarget(null);
    setDeletePassword("");
  }

  const withoutOfficial = (detail?.offspring ?? lambRows).filter((lamb) => !lamb.official_tag).length;

  function openTool(next: Exclude<Tool, null>) {
    if (next !== "event") {
      setEditingEventId(null);
    }
    setTool(next);
    setInfo(null);
  }

  function closeTool() {
    setTool(null);
    setEditingEventId(null);
    setEventForm({ mother_tag: "", birth_date: todayIso(), password: "" });
    setLambs([emptyLamb()]);
    setWeightForm({ working_tag: "", weight_kg: "", password: "" });
    setAssignPassword("");
  }

  function closeNotes() {
    setNotesLamb(null);
    setNoteText("");
    setNotePassword("");
  }

  function closeWeights() {
    setWeightsLamb(null);
    setWeightsValues([]);
    setWeightsPassword("");
  }

  function toolModalTitle(): string {
    switch (tool) {
      case "rules":
        return "Reguły ważenia";
      case "weight":
        return "Szybkie wagi";
      case "mothers":
        return "Matki bez wykotu";
      case "assign":
        return "Przydziel kolczyki urzędowe";
      case "event":
        return editingEventId != null
          ? `Edytuj wykot — ${detail?.name ?? ""}`
          : `Odnotuj wykot — ${detail?.name ?? ""}`;
      case "quick":
        return `Szybkie wykoty — ${detail?.name ?? ""}`;
      default:
        return "";
    }
  }

  function toolModalSubmit(): ((event: FormEvent) => void) | undefined {
    switch (tool) {
      case "rules":
        return onSaveRules;
      case "weight":
        return onQuickWeight;
      case "assign":
        return onAssignTags;
      case "event":
      case "quick":
        return onCreateEvent;
      default:
        return undefined;
    }
  }

  async function onCreateEvent(event: FormEvent) {
    event.preventDefault();
    if (!detail) {
      return;
    }
    const payloadLambs = lambs
      .map((lamb) => {
        const base: {
          id?: number;
          sex: Sex;
          working_tag: string;
          official_tag: string | null;
          father_tag: string | null;
        } = {
          sex: lamb.sex,
          working_tag: lamb.working_tag.trim(),
          official_tag: normalizeTag(lamb.official_tag) || null,
          father_tag: lamb.father_tag.trim() || null,
        };
        if (editingEventId != null && lamb.id != null) {
          base.id = lamb.id;
        }
        return base;
      })
      .filter((lamb) => lamb.working_tag);
    if (!eventForm.mother_tag.trim() || payloadLambs.length === 0) {
      setError("Podaj matkę i co najmniej jedno jagnię z numerem roboczym.");
      return;
    }
    const freeSet = new Set(
      (options?.free_official_tags ?? []).map((tag) => normalizeTag(tag)).filter(Boolean),
    );
    const officialSeen = new Set<string>();
    for (const lamb of payloadLambs) {
      const official = lamb.official_tag;
      if (!official) {
        continue;
      }
      if (officialSeen.has(official)) {
        setError("Numery oficjalne jagniąt w wykocie muszą być unikalne.");
        return;
      }
      officialSeen.add(official);
      const keptFromEdit =
        lamb.id != null &&
        detail?.offspring.some(
          (existing) =>
            existing.id === lamb.id && normalizeTag(existing.official_tag || "") === official,
        );
      if (!keptFromEdit && !freeSet.has(official)) {
        setError(
          `Numer oficjalny „${official}” nie istnieje w puli Wolne kolczyki.`,
        );
        return;
      }
    }
    const password = skipPassword ? null : passwordOrNull(eventForm.password);
    const verifyError = await requireOfflineAdminPassword(password);
    if (verifyError) {
      setError(verifyError);
      return;
    }
    
    const body = {
      password,
      mother_tag: eventForm.mother_tag.trim(),
      birth_date: eventForm.birth_date,
      offspring: payloadLambs,
    };
    const wasEditing = editingEventId != null;
    setBusy(true);
    
    const result = wasEditing
      ? await mutateOrQueue(
          "PATCH",
          `/api/birth-events/${editingEventId}`,
          body
        )
      : await mutateOrQueue(
          "POST",
          `/api/birth-seasons/${detail.id}/events`,
          body
        );
    
    setBusy(false);
    
    if (!result.success) {
      setError(
        apiError(
          result.error,
          wasEditing
            ? "Nie udało się zaktualizować wykotu."
            : "Nie udało się odnotować wykotu.",
        ),
      );
      return;
    }
    
    if (password && result.online) {
      await storeAdminPasswordVerifier(password);
    } else if (!result.online && !wasEditing) {
      const tempEventId = -Date.now();
      const optimisticOffspring: Offspring[] = payloadLambs.map((lamb, index) => ({
        id: tempEventId - index - 1,
        event_id: tempEventId,
        sex: lamb.sex,
        working_tag: lamb.working_tag,
        official_tag: lamb.official_tag,
        father_tag: lamb.father_tag,
        mother_tag: eventForm.mother_tag.trim(),
        birth_date: eventForm.birth_date,
      }));
      const optimisticEvent: BirthEvent = {
        id: tempEventId,
        season_id: detail.id,
        mother_tag: eventForm.mother_tag.trim(),
        birth_date: eventForm.birth_date,
        offspring: optimisticOffspring,
      };
      await patchReplica<BirthSeasonDetail>(`birth-season-detail:${detail.id}`, (current) => {
        const base = current ?? detail;
        return {
          ...base,
          events: [...base.events, optimisticEvent],
          offspring: [...base.offspring, ...optimisticOffspring],
          event_count: base.event_count + 1,
          offspring_count: base.offspring_count + optimisticOffspring.length,
        };
      });
    }
    setEventForm({ mother_tag: "", birth_date: todayIso(), password: "" });
    setLambs([emptyLamb()]);
    setEditingEventId(null);
    if (tool !== "quick") {
      setTool(null);
    }
    setInfo(
      result.online
        ? wasEditing
          ? "Zaktualizowano wykot."
          : "Zapisano wykot."
        : offlineQueuedInfo(result.needsPassword),
    );
    await loadDetail(detail.id);
    await loadSeasons(detail.id);
  }

  function suggestedFather(mother: string): string {
    return options?.suggested_father_by_mother?.[mother] || "";
  }

  function defaultFatherForNewLamb(): string {
    const fromSuggestion = suggestedFather(eventForm.mother_tag);
    if (fromSuggestion) {
      return fromSuggestion;
    }
    return lambs.find((lamb) => lamb.father_tag.trim())?.father_tag.trim() || "";
  }

  function fatherSelectOptions(): { tag: string; color: string | null }[] {
    const breedingRams = options?.breeding_sires ?? [];
    if (breedingRams.length > 0) {
      return breedingRams.map((ram) => ({ tag: ram.tag, color: ram.color }));
    }
    return (options?.sire_tags ?? []).map((tag) => ({ tag, color: null }));
  }

  function officialSelectOptions(lambIndex: number): string[] {
    const preferred = normalizeTag(lambs[lambIndex]?.official_tag || "");
    const taken = new Set(
      lambs
        .map((lamb, index) =>
          index === lambIndex ? "" : normalizeTag(lamb.official_tag),
        )
        .filter(Boolean),
    );
    const available = (options?.free_official_tags ?? [])
      .map((tag) => normalizeTag(tag))
      .filter((tag) => tag && (!taken.has(tag) || tag === preferred));
    if (preferred && !available.includes(preferred)) {
      available.unshift(preferred);
    }
    return [...new Set(available)].sort((left, right) => left.localeCompare(right, "pl"));
  }

  function openEventForMother(tag: string) {
    const father = suggestedFather(tag);
    setEditingEventId(null);
    setEventForm({ ...eventForm, mother_tag: tag });
    setLambs([emptyLamb(father)]);
    setTool("event");
  }

  function openEditEvent(eventId: number) {
    const lambingEvent = detail?.events.find((item) => item.id === eventId);
    if (!lambingEvent) {
      setError("Nie znaleziono wykotu do edycji.");
      return;
    }
    setEditingEventId(eventId);
    setEventForm({
      mother_tag: lambingEvent.mother_tag,
      birth_date: lambingEvent.birth_date,
      password: "",
    });
    setLambs(
      lambingEvent.offspring.length
        ? lambingEvent.offspring.map((lamb) => ({
            id: lamb.id,
            sex: lamb.sex,
            working_tag: lamb.working_tag,
            official_tag: lamb.official_tag || "",
            father_tag: lamb.father_tag || "",
          }))
        : [emptyLamb()],
    );
    setTool("event");
    setInfo(null);
  }

  function openNotes(lamb: Offspring) {
    const fromDetail = detail?.offspring.find((item) => item.id === lamb.id) ?? lamb;
    setNotesLamb(fromDetail);
    setNoteText("");
    setNotePassword("");
    setError(null);
  }

  function openWeights(lamb: Offspring) {
    const count = detail?.weight_count ?? 0;
    if (count < 1) {
      return;
    }
    const fromDetail = detail?.offspring.find((item) => item.id === lamb.id) ?? lamb;
    setWeightsLamb(fromDetail);
    setWeightsValues(
      Array.from({ length: count }, (_, index) => {
        const entry = fromDetail.weights?.find(
          (weight) => weight.weight_index === index + 1,
        );
        return entry?.weight_kg != null ? String(entry.weight_kg) : "";
      }),
    );
    setWeightsPassword("");
    setError(null);
  }

  async function onSaveWeights(event: FormEvent) {
    event.preventDefault();
    if (!weightsLamb) {
      return;
    }
    if (!skipPassword && !weightsPassword.trim()) {
      setError("Podaj hasło administratora.");
      return;
    }
    const weights = weightsValues.map((raw, index) => {
      const trimmed = raw.trim().replace(",", ".");
      return {
        weight_index: index + 1,
        weight_kg: trimmed === "" ? null : Number(trimmed),
        weighed_at: null,
      };
    });
    if (weights.some((item) => item.weight_kg != null && !Number.isFinite(item.weight_kg))) {
      setError("Podaj poprawne wartości wag (kg).");
      return;
    }
    setBusy(true);
    const result = await api.PATCH("/api/offspring/{offspring_id}", {
      params: { path: { offspring_id: weightsLamb.id } },
      body: {
        password: skipPassword ? null : passwordOrNull(weightsPassword),
        weights,
      },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się zapisać wag."));
      return;
    }
    setInfo(`Zapisano wagi jagnięcia ${formatTag(weightsLamb.working_tag)}.`);
    closeWeights();
    if (detail) {
      await loadDetail(detail.id);
    }
  }

  async function onAddNote(event: FormEvent) {
    event.preventDefault();
    if (!notesLamb || !noteText.trim()) {
      setError("Podaj treść notatki.");
      return;
    }
    setBusy(true);
    const result = await api.POST("/api/offspring/{offspring_id}/notes", {
      params: { path: { offspring_id: notesLamb.id } },
      body: {
        password: skipPassword ? null : passwordOrNull(notePassword),
        text: noteText.trim(),
      },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się dodać notatki."));
      return;
    }
    const updated = result.data as Offspring;
    setNotesLamb(updated);
    setNoteText("");
    setInfo("Dodano notatkę.");
    if (detail) {
      await loadDetail(detail.id);
    }
  }

  async function loadPending() {
    if (!detail) {
      return;
    }
    setBusy(true);
    const result = await loadWithReplica<{ mothers?: PendingMother[]; count?: number }>(
      `pending-mothers:${detail.id}`,
      () => api.GET("/api/birth-seasons/{season_id}/pending-mothers", {
        params: { path: { season_id: detail.id } },
      })
    );
    setBusy(false);
    if (result.error && !result.fromReplica) {
      setError(apiError(result.error, "Nie udało się wczytać matek bez wykotu."));
      return;
    }
    const data = result.data;
    setPending(data?.mothers ?? []);
    setTool("mothers");
  }

  async function onQuickWeight(event: FormEvent) {
    event.preventDefault();
    if (!detail) {
      return;
    }
    
    const password = passwordOrNull(weightForm.password);
    const verifyError = await requireOfflineAdminPassword(password);
    if (verifyError) {
      setError(verifyError);
      return;
    }
    
    const body = {
      password,
      working_tag: weightForm.working_tag.trim(),
      weight_kg: Number(weightForm.weight_kg),
    };
    
    setBusy(true);
    const result = await mutateOrQueue(
      "POST",
      `/api/birth-seasons/${detail.id}/quick-weight`,
      body
    );
    setBusy(false);
    
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się zapisać wagi."));
      return;
    }
    
    // Sukces online - zapisz verifier hasła
    if (password && result.online) {
      await storeAdminPasswordVerifier(password);
    }
    
    const data = result.online ? (result.data as { message?: string } | undefined) : undefined;
    setInfo(
      result.online
        ? data?.message || "Zapisano wagę."
        : offlineQueuedInfo(result.needsPassword),
    );
    setWeightForm({ working_tag: "", weight_kg: "", password: "" });
    await loadDetail(detail.id);
  }

  async function onSaveRules(event: FormEvent) {
    event.preventDefault();
    if (!detail) {
      return;
    }
    const allRule =
      rules.type === "ages"
        ? {
            type: "ages",
            ages_days: rules.ages
              .split(/[,\s]+/)
              .map((part) => Number(part))
              .filter((value) => Number.isFinite(value) && value > 0),
          }
        : {
            type: "interval",
            min_age_days: Number(rules.min) || 0,
            every_days: Number(rules.every) || 1,
          };
    setBusy(true);
    const result = await api.PUT("/api/birth-seasons/{season_id}/weighing-schedule", {
      params: { path: { season_id: detail.id } },
      body: { scope: "all", all_rule: allRule },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się zapisać reguł ważenia."));
      return;
    }
    setInfo("Zapisano reguły ważenia.");
    setTool(null);
    await loadDetail(detail.id);
  }

  async function onClearRules() {
    if (!detail) {
      return;
    }
    setBusy(true);
    const result = await api.DELETE("/api/birth-seasons/{season_id}/weighing-schedule", {
      params: { path: { season_id: detail.id } },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się usunąć reguł ważenia."));
      return;
    }
    setInfo("Usunięto reguły ważenia.");
    setTool(null);
    await loadDetail(detail.id);
  }

  async function onAssignTags(event: FormEvent) {
    event.preventDefault();
    if (!detail) {
      return;
    }
    
    const password = passwordOrNull(assignPassword);
    const verifyError = await requireOfflineAdminPassword(password);
    if (verifyError) {
      setError(verifyError);
      return;
    }
    
    setBusy(true);
    const result = await mutateOrQueue(
      "POST",
      `/api/birth-seasons/${detail.id}/assign-official-tags`,
      { password }
    );
    setBusy(false);
    
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się przydzielić kolczyków."));
      return;
    }
    
    // Sukces online - zapisz verifier hasła
    if (password && result.online) {
      await storeAdminPasswordVerifier(password);
    }
    
    const data = result.online ? (result.data as { message?: string; assigned_count?: number } | undefined) : undefined;
    setInfo(
      result.online
        ? data?.message || `Przydzielono ${data?.assigned_count ?? 0} kolczyków.`
        : offlineQueuedInfo(result.needsPassword),
    );
    setAssignPassword("");
    setTool(null);
    await loadDetail(detail.id);
  }

  async function confirmDelete() {
    if (!deleteTarget) {
      return;
    }
    const password = skipPassword ? null : passwordOrNull(deletePassword);
    const verifyError = await requireOfflineAdminPassword(password);
    if (verifyError) {
      setError(verifyError);
      return;
    }
    
    setBusy(true);
    const result =
      deleteTarget.kind === "lamb"
        ? await mutateOrQueue(
            "DELETE",
            `/api/offspring/${deleteTarget.lamb.id}`,
            { password }
          )
        : await mutateOrQueue(
            "DELETE",
            `/api/birth-events/${deleteTarget.eventId}`,
            { password }
          );
    setBusy(false);
    
    if (!result.success) {
      setError(
        apiError(
          result.error,
          deleteTarget.kind === "lamb"
            ? "Nie udało się usunąć jagnięcia."
            : "Nie udało się usunąć wykotu.",
        ),
      );
      return;
    }
    
    // Sukces online - zapisz verifier hasła
    if (password && result.online) {
      await storeAdminPasswordVerifier(password);
    }
    
    const kind = deleteTarget.kind;
    closeDeleteModal();
    setInfo(
      result.online
        ? kind === "lamb"
          ? "Usunięto jagnię."
          : "Usunięto wykot matki."
        : offlineQueuedInfo(result.needsPassword),
    );
    if (detail) {
      await loadDetail(detail.id);
      await loadSeasons(detail.id);
    }
  }

  async function confirmIrz() {
    if (!irzTarget) {
      return;
    }
    if (irzTarget.register && !irzTarget.lamb.official_tag) {
      setError("Najpierw przydziel numer oficjalny.");
      return;
    }
    const password = skipPassword ? null : passwordOrNull(irzPassword);
    setBusy(true);
    const result = await api.PATCH("/api/offspring/{offspring_id}", {
      params: { path: { offspring_id: irzTarget.lamb.id } },
      body: {
        password,
        irz_registered: irzTarget.register,
      },
    });
    setBusy(false);
    if (result.error) {
      setError(
        apiError(
          result.error,
          irzTarget.register
            ? "Nie udało się oznaczyć rejestracji IRZ."
            : "Nie udało się cofnąć statusu IRZ.",
        ),
      );
      return;
    }
    const registered = irzTarget.register;
    setIrzTarget(null);
    setIrzPassword("");
    setInfo(
      registered
        ? "Oznaczono jagnię jako zarejestrowane w IRZ. Kolczyk usunięto z puli wolnych."
        : "Cofnięto status IRZ. Numer oficjalny nie wraca do puli wolnych.",
    );
    if (detail) {
      await loadDetail(detail.id);
    }
  }

  const weighing = detail?.weighing_status;

  return (
    <main className="page" data-testid="lambings-page">
      <header className="page__header">
        <p className="page__kicker">Rozród</p>
        <h1>Wykoty</h1>
      </header>

      <BirthsSubnav area="births" />

      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      <AlertBanner variant="ok" message={info} onDismiss={() => setInfo(null)} />

      <SeasonMemberPicker
        open={showMembers}
        onClose={() => setShowMembers(false)}
        title="Edytuj skład grupy"
        members={rosterMembers}
        eligible={eligible}
        busy={busy}
        testId="births-members-form"
        testIdPrefix="births"
        onSave={saveMembers}
      />

      <div className="season-picker" data-testid="birth-season-bar">
        {seasons.length === 0 ? (
          <p className="muted season-picker__empty">
            Brak aktywnych grup — utwórz je w{" "}
            <Link to="/reproduction">Aktywne grupy</Link>.
          </p>
        ) : (
          <label className="season-picker__field">
            <span className="season-picker__label">Grupa wykotów</span>
            <select
              className="input"
              value={selectedId ?? ""}
              aria-label="Wybierz grupę wykotów"
              data-testid="birth-season-select"
              onChange={(event) => {
                const nextId = Number(event.target.value);
                if (!Number.isFinite(nextId)) {
                  return;
                }
                setSelectedId(nextId);
                setSearchParams({ seasonId: String(nextId) });
                closeTool();
                void loadDetail(nextId);
              }}
            >
              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name}
                  {season.breed ? ` · ${season.breed}` : ""}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {detail ? (
        <>
          <section className="panel">
            <div className="panel__header">
              <div>
                <h2 className="panel__title">{detail.name}</h2>
                <p className="muted">
                  {detail.breed || "Bez rasy"} · wykoty {detail.event_count} · jagnięta{" "}
                  {detail.offspring_count}
                  {withoutOfficial ? ` · bez kolczyka urzędowego ${withoutOfficial}` : ""}
                </p>
              </div>
              <div className="panel__header-actions">
                {detail.include_mating === true ? (
                  <Link
                    className="btn btn--ghost"
                    to={`/reproduction/breeding?seasonId=${detail.breeding_season_id ?? ""}`}
                  >
                    Edytuj skład w Stanówce
                  </Link>
                ) : (
                  <button
                    className="btn btn--ghost"
                    type="button"
                    data-testid="births-edit-members"
                    onClick={() => void openMembers()}
                  >
                    Edytuj grupę
                  </button>
                )}
                <button className="btn btn--ghost" type="button" onClick={() => openTool("rules")}>
                  Reguły ważenia
                </button>
                <button className="btn btn--ghost" type="button" onClick={() => void loadPending()}>
                  Matki bez wykotu
                </button>
                <button className="btn btn--ghost" type="button" onClick={() => openTool("quick")}>
                  Szybkie wykoty
                </button>
                <button className="btn btn--ghost" type="button" onClick={() => openTool("weight")}>
                  Szybkie wagi
                </button>
                <button
                  className="btn btn--ghost"
                  type="button"
                  disabled={!withoutOfficial}
                  onClick={() => openTool("assign")}
                >
                  Przydziel kolczyki
                </button>
                <button
                  className="btn"
                  type="button"
                  data-testid="lambing-add"
                  onClick={() => {
                    setEditingEventId(null);
                    setEventForm({
                      mother_tag: "",
                      birth_date: todayIso(),
                      password: "",
                    });
                    setLambs([emptyLamb()]);
                    setTool("event");
                    setInfo(null);
                  }}
                >
                  Odnotuj wykot
                </button>
              </div>
            </div>

            {weighing?.has_schedule ? (
              <p
                className={`banner ${weighing.overdue_count > 0 ? "banner--warn" : "banner--ok"}`}
                role="status"
              >
                Ważenia: zaległe {weighing.overdue_count}, najbliższy termin{" "}
                {formatDate(weighing.next_due_date)} ({weighing.next_due_count} szt.).
              </p>
            ) : null}

            <div className="filters">
              <input
                className="input"
                type="search"
                placeholder="Szukaj matki…"
                value={filters.mother_tag ?? ""}
                onChange={(event) => setFilter("mother_tag", event.target.value)}
                {...motherFilterProps}
              />
              <select
                className="input"
                value={filters.sex ?? ""}
                onChange={(event) => setFilter("sex", event.target.value)}
                aria-label="Filtruj płeć"
              >
                <option value="">Płeć: wszystkie</option>
                <option value="female">Samica</option>
                <option value="male">Samiec</option>
              </select>
              <input
                className="input"
                type="search"
                placeholder="Nr roboczy…"
                value={filters.working_tag ?? ""}
                onChange={(event) => setFilter("working_tag", event.target.value)}
                {...workingFilterProps}
              />
            </div>

            <div className="table-wrap lambings__table-wrap">
              <ResponsiveTable className="tbl lambings__table" data-testid="lambings-table">
                <thead>
                  <tr>
                    <th>
                      <SortHeader
                        label="Data"
                        sortKey="birth_date"
                        activeKey={groupSortKey}
                        sortDir={groupSortDir}
                        onToggle={() => toggleGroupSort("birth_date")}
                      />
                    </th>
                    <th>
                      <SortHeader
                        label="Matka"
                        sortKey="mother_tag"
                        activeKey={groupSortKey}
                        sortDir={groupSortDir}
                        onToggle={() => toggleGroupSort("mother_tag")}
                      />
                    </th>
                    <th>
                      <SortHeader
                        label="Płeć"
                        sortKey="sex"
                        activeKey={withinSortKey}
                        sortDir={withinSortDir}
                        onToggle={toggleWithinSort}
                      />
                    </th>
                    <th>
                      <SortHeader
                        label="Nr roboczy"
                        sortKey="working_tag"
                        activeKey={withinSortKey}
                        sortDir={withinSortDir}
                        onToggle={toggleWithinSort}
                      />
                    </th>
                    <th>
                      <SortHeader
                        label="Nr oficjalny"
                        sortKey="official_tag"
                        activeKey={withinSortKey}
                        sortDir={withinSortDir}
                        onToggle={toggleWithinSort}
                      />
                    </th>
                    <th>IRZ</th>
                    <th>
                      <SortHeader
                        label="Ojciec"
                        sortKey="father_tag"
                        activeKey={withinSortKey}
                        sortDir={withinSortDir}
                        onToggle={toggleWithinSort}
                      />
                    </th>
                    {weightCount > 0
                      ? Array.from({ length: weightCount }, (_, index) => {
                          const weightIndex = index + 1;
                          return (
                            <th key={`weight-h-${weightIndex}`}>
                              <SortHeader
                                label={`Waga ${weightIndex}`}
                                sortKey={`weight_${weightIndex}`}
                                activeKey={withinSortKey}
                                sortDir={withinSortDir}
                                onToggle={toggleWithinSort}
                              />
                            </th>
                          );
                        })
                      : null}
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {motherGroups.length === 0 ? (
                    <tr className="tbl__empty-row">
                      <td colSpan={tableColSpan} className="muted">
                        {hasActiveFilters
                          ? "Brak jagniąt pasujących do filtrów."
                          : "Brak wykotów w tej grupie."}
                      </td>
                    </tr>
                  ) : (
                    motherGroups.flatMap((group) => {
                      const groupOverdue = group.offspring.some((lamb) => lamb.weight_overdue);
                      return [
                      <tr
                        key={`group-${group.motherTag}`}
                        className={`tbl__group-row${groupOverdue ? " lambings__row--overdue" : ""}`}
                      >
                        <td colSpan={tableColSpan}>
                          <div className="lambings__group-row">
                            <span className="lambings__group-label">
                              Matka: <EarTag value={group.motherTag} />
                              <span className="muted">
                                {" "}
                                · {group.offspring.length}{" "}
                                {group.offspring.length === 1 ? "jagnię" : "jagniąt"}
                              </span>
                              {groupOverdue ? (
                                <span className="lambings__overdue-badge"> · zaległe ważenie</span>
                              ) : null}
                            </span>
                            <div className="actions">
                              {group.eventId ? (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn--sm"
                                    data-testid="lambing-edit"
                                    disabled={busy}
                                    onClick={() => openEditEvent(group.eventId!)}
                                  >
                                    Edytuj wykot
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn--ghost btn--sm"
                                    disabled={busy}
                                    data-testid="lambing-delete-event"
                                    onClick={() => {
                                      setError(null);
                                      setDeletePassword("");
                                      setDeleteTarget({
                                        kind: "event",
                                        eventId: group.eventId!,
                                        motherTag: group.motherTag,
                                      });
                                    }}
                                  >
                                    Usuń wykot
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </div>
                        </td>
                      </tr>,
                      ...group.offspring.map((lamb) => (
                        <tr
                          key={lamb.id}
                          className={
                            lamb.weight_overdue
                              ? "lambings__lamb-row lambings__row--overdue"
                              : "lambings__lamb-row"
                          }
                          title={lamb.weight_overdue ? "Zaległe ważenie" : undefined}
                        >
                          <td className="lambings__cell lambings__cell--date">
                            {formatDate(lamb.birth_date)}
                          </td>
                          <td className="lambings__cell lambings__cell--mother">
                            {lamb.mother_tag ? <EarTag value={lamb.mother_tag} /> : "—"}
                          </td>
                          <td className="lambings__cell lambings__cell--sex">
                            {SEX_LABELS[lamb.sex] || lamb.sex}
                          </td>
                          <td className="lambings__cell lambings__cell--working" data-label="Nr roboczy">
                            <strong>
                              <EarTag value={lamb.working_tag} />
                            </strong>
                          </td>
                          <td className="lambings__cell lambings__cell--official">
                            {lamb.official_tag ? <EarTag value={lamb.official_tag} /> : "—"}
                          </td>
                          <td className="lambings__cell lambings__cell--irz">
                            {lamb.irz_registered ? (
                              <span className="lambings__overdue-badge" title="Zarejestrowane w IRZ">
                                Tak
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="lambings__cell lambings__cell--father">
                            {lamb.father_tag ? (
                              <span className="breeding-mating">
                                {(() => {
                                  const color = options?.breeding_sires?.find(
                                    (ram) => ram.tag === lamb.father_tag,
                                  )?.color;
                                  return color ? (
                                    <span
                                      className={`ram-swatch ram-swatch--${color}`}
                                      title={sireColorLabel(color)}
                                      aria-hidden="true"
                                    />
                                  ) : null;
                                })()}
                                <span>
                                  <EarTag value={lamb.father_tag} />
                                </span>
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          {weightCount > 0
                            ? Array.from({ length: weightCount }, (_, index) => {
                                const weightIndex = index + 1;
                                const entry = lamb.weights?.find(
                                  (weight) => weight.weight_index === weightIndex,
                                );
                                return (
                                  <td
                                    key={`weight-${lamb.id}-${weightIndex}`}
                                    className="lambings__cell lambings__cell--weight"
                                  >
                                    {entry?.weight_kg != null ? `${entry.weight_kg} kg` : "—"}
                                  </td>
                                );
                              })
                            : null}
                          <td className="lambings__cell lambings__cell--actions">
                            <div className="actions">
                              {weightCount > 0 ? (
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--sm"
                                  data-testid="lambing-weigh"
                                  disabled={busy}
                                  onClick={() => openWeights(lamb)}
                                >
                                  Ważenie
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                data-testid="lambing-irz"
                                disabled={
                                  busy ||
                                  !LAMB_IRZ_REGISTRATION_ENABLED ||
                                  (!lamb.irz_registered && !lamb.official_tag)
                                }
                                title={
                                  !LAMB_IRZ_REGISTRATION_ENABLED
                                    ? LAMB_IRZ_DISABLED_TOOLTIP
                                    : !lamb.irz_registered && !lamb.official_tag
                                      ? "Przypisz kolczyk urzędowy przed rejestracją IRZ."
                                      : undefined
                                }
                                onClick={() => {
                                  if (!LAMB_IRZ_REGISTRATION_ENABLED) return;
                                  setError(null);
                                  setIrzPassword("");
                                  setIrzTarget({
                                    lamb,
                                    register: !lamb.irz_registered,
                                  });
                                }}
                              >
                                {lamb.irz_registered ? "Cofnij IRZ" : "IRZ"}
                              </button>
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                data-testid="lambing-notes"
                                disabled={busy}
                                onClick={() => openNotes(lamb)}
                              >
                                Notatki
                                {Array.isArray(lamb.notes) ? ` (${lamb.notes.length})` : ""}
                              </button>
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                data-testid="lambing-delete-lamb"
                                disabled={busy}
                                onClick={() => {
                                  setError(null);
                                  setDeletePassword("");
                                  setDeleteTarget({ kind: "lamb", lamb });
                                }}
                              >
                                Usuń
                              </button>
                            </div>
                          </td>
                        </tr>
                      )),
                    ];
                    })
                  )}
                </tbody>
              </ResponsiveTable>
            </div>
          </section>
        </>
      ) : null}

      <FormModal
        open={tool != null}
        onClose={closeTool}
        title={toolModalTitle()}
        size={tool === "event" || tool === "quick" ? "xl" : "md"}
        testId={tool === "event" || tool === "quick" ? "lambing-form" : undefined}
        className={tool === "event" || tool === "quick" ? "lambing-event-modal" : undefined}
        onSubmit={toolModalSubmit()}
      >
        {tool === "rules" ? (
          <>
            {weightCount < 1 ? (
              <p className="muted" data-testid="births-weighing-disabled-hint">
                Ta grupa nie ma włączonych ważeń jagniąt. Ustaw liczbę ważeń w Rozród →
                Aktywne grupy → Edytuj, a potem wróć tu ustawić terminy.
              </p>
            ) : null}
            <Field label="Typ">
              <select
                className="input"
                value={rules.type}
                onChange={(event) =>
                  setRules({ ...rules, type: event.target.value as "ages" | "interval" })
                }
                disabled={weightCount < 1}
              >
                <option value="ages">Wiek w dniach</option>
                <option value="interval">Co N dni</option>
              </select>
            </Field>
            {rules.type === "ages" ? (
              <Field label="Dni życia (po przecinku)">
                <input
                  className="input"
                  value={rules.ages}
                  onChange={(event) => setRules({ ...rules, ages: event.target.value })}
                  disabled={weightCount < 1}
                />
              </Field>
            ) : (
              <div className="form__row">
                <Field label="Od dnia życia">
                  <input
                    className="input"
                    type="number"
                    min={0}
                    value={rules.min}
                    onChange={(event) => setRules({ ...rules, min: event.target.value })}
                    disabled={weightCount < 1}
                  />
                </Field>
                <Field label="Co ile dni">
                  <input
                    className="input"
                    type="number"
                    min={1}
                    value={rules.every}
                    onChange={(event) => setRules({ ...rules, every: event.target.value })}
                    disabled={weightCount < 1}
                  />
                </Field>
              </div>
            )}
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={closeTool} disabled={busy}>
                Anuluj
              </button>
              <button
                className="btn btn--ghost"
                type="button"
                disabled={busy || weightCount < 1}
                onClick={() => void onClearRules()}
              >
                Usuń reguły
              </button>
              <button className="btn" type="submit" disabled={busy || weightCount < 1}>
                Zapisz reguły
              </button>
            </div>
          </>
        ) : null}

        {tool === "weight" ? (
          <>
            <div className="form__row">
              <Field label="Nr roboczy">
                <input
                  className="input"
                  value={weightForm.working_tag}
                  onChange={(event) =>
                    setWeightForm({ ...weightForm, working_tag: event.target.value })
                  }
                  required
                  autoComplete="off"
                />
              </Field>
              <Field label="Waga (kg)">
                <input
                  className="input"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={weightForm.weight_kg}
                  onChange={(event) =>
                    setWeightForm({ ...weightForm, weight_kg: event.target.value })
                  }
                  required
                />
              </Field>
            </div>
            <Field label="Hasło administratora">
              <input
                className="input"
                type="password"
                value={weightForm.password}
                onChange={(event) =>
                  setWeightForm({ ...weightForm, password: event.target.value })
                }
                {...passwordFieldProps(`${fieldNs}-weight-password`)}
              />
            </Field>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={closeTool} disabled={busy}>
                Anuluj
              </button>
              <button className="btn" type="submit" disabled={busy}>
                Zapisz wagę
              </button>
            </div>
          </>
        ) : null}

        {tool === "mothers" ? (
          <>
            {pending.length === 0 ? (
              <p className="muted">W tej grupie wszystkie matki ze stanówki mają wykot.</p>
            ) : (
              <>
                <p className="muted">
                  Posortowane według planowanej daty wykotu (najbliższe na górze).
                </p>
                <ul className="list" data-testid="lambing-pending-mothers">
                  {pending.map((mother) => (
                    <li key={mother.tag} className="list__item">
                      <div>
                        <strong>
                          <EarTag value={mother.tag} />
                        </strong>
                        <p className="muted">
                          {mother.planned_birth_date
                            ? `Planowany wykot ${formatDate(mother.planned_birth_date)}`
                            : "Brak planowanej daty wykotu"}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn btn--sm"
                        onClick={() => openEventForMother(mother.tag)}
                      >
                        Odnotuj
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={closeTool}>
                Anuluj
              </button>
            </div>
          </>
        ) : null}

        {tool === "assign" ? (
          <>
            <p className="muted">
              Jagnięta bez numeru oficjalnego: {withoutOfficial}. Kolczyki zostaną wzięte z listy
              wolnych.
            </p>
            <Field label="Hasło administratora">
              <input
                className="input"
                type="password"
                value={assignPassword}
                onChange={(event) => setAssignPassword(event.target.value)}
                {...passwordFieldProps(`${fieldNs}-assign-password`)}
              />
            </Field>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={closeTool} disabled={busy}>
                Anuluj
              </button>
              <button className="btn" type="submit" disabled={busy}>
                Przydziel
              </button>
            </div>
          </>
        ) : null}

        {(tool === "event" || tool === "quick") && detail ? (
          <>
            <div className="lambing-event-body">
              {tool === "quick" && editingEventId == null ? (
                <p className="muted">
                  Po zapisie formularz zostaje otwarty, żeby odnotować kolejną matkę.
                </p>
              ) : null}

              <section className="lambing-event-section">
                <h4 className="lambing-event-section__title">Matka i data</h4>
                <div className="form__row">
                  <Field label="Matka">
                    {options?.breeding_dams?.length ? (
                      <select
                        className="input"
                        data-testid="lambing-mother"
                        value={eventForm.mother_tag}
                        onChange={(event) => {
                          const mother = event.target.value;
                          const father = suggestedFather(mother);
                          setEventForm({ ...eventForm, mother_tag: mother });
                          setLambs((current) =>
                            current.map((lamb) => ({
                              ...lamb,
                              father_tag: father || lamb.father_tag,
                            })),
                          );
                        }}
                        required
                      >
                        <option value="">Wybierz matkę…</option>
                        {options.breeding_dams.map((ewe) => (
                          <option key={ewe.tag} value={ewe.tag}>
                            {formatTag(ewe.tag)}
                            {ewe.working_number ? ` (${ewe.working_number})` : ""}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="input"
                        data-testid="lambing-mother"
                        value={eventForm.mother_tag}
                        onChange={(event) => {
                          const mother = event.target.value;
                          const father = suggestedFather(mother);
                          setEventForm({ ...eventForm, mother_tag: mother });
                          if (father) {
                            setLambs((current) =>
                              current.map((lamb) => ({
                                ...lamb,
                                father_tag: father,
                              })),
                            );
                          }
                        }}
                        required
                        autoComplete="off"
                      />
                    )}
                  </Field>
                  <Field label="Data urodzenia">
                    <input
                      className="input"
                      type="date"
                      value={eventForm.birth_date}
                      onChange={(event) =>
                        setEventForm({ ...eventForm, birth_date: event.target.value })
                      }
                      required
                    />
                  </Field>
                </div>
              </section>

              <section className="lambing-event-section">
                <div className="lambing-event-section__header">
                  <h4 className="lambing-event-section__title">
                    Jagnięta ({lambs.length})
                  </h4>
                  <div className="actions">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() =>
                        setLambs((current) => [
                          ...current,
                          emptyLamb(defaultFatherForNewLamb()),
                        ])
                      }
                    >
                      Dodaj jagnię
                    </button>
                    {lambs.length > 1 ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setLambs((current) => current.slice(0, -1))}
                      >
                        Usuń ostatnie
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="lambing-lamb-list">
                  {lambs.map((lamb, index) => (
                    <article key={index} className="lambing-lamb-card">
                      <p className="lambing-lamb-card__title">Jagnię {index + 1}</p>
                      <div className="lambing-lamb-card__grid">
                        <Field label="Płeć">
                          <select
                            className="input"
                            value={lamb.sex}
                            onChange={(event) => {
                              const next = [...lambs];
                              next[index] = { ...lamb, sex: event.target.value as Sex };
                              setLambs(next);
                            }}
                          >
                            <option value="female">Samica</option>
                            <option value="male">Samiec</option>
                          </select>
                        </Field>
                        <Field label="Nr roboczy">
                          <input
                            className="input"
                            data-testid="lamb-working"
                            value={lamb.working_tag}
                            onChange={(event) => {
                              const next = [...lambs];
                              next[index] = { ...lamb, working_tag: event.target.value };
                              setLambs(next);
                            }}
                            required
                            autoComplete="off"
                          />
                        </Field>
                        <Field label="Nr oficjalny">
                          <select
                            className="input"
                            data-testid="lamb-official"
                            aria-label="Numer oficjalny z puli Wolne kolczyki"
                            value={lamb.official_tag}
                            onChange={(event) => {
                              const next = [...lambs];
                              next[index] = { ...lamb, official_tag: event.target.value };
                              setLambs(next);
                            }}
                          >
                            <option value="">
                              {officialSelectOptions(index).length || lamb.official_tag
                                ? "— brak —"
                                : "Brak wolnych kolczyków"}
                            </option>
                            {officialSelectOptions(index).map((tag) => (
                              <option key={tag} value={tag}>
                                {formatTag(tag)}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Ojciec">
                          <select
                            className="input"
                            value={lamb.father_tag}
                            onChange={(event) => {
                              const next = [...lambs];
                              next[index] = { ...lamb, father_tag: event.target.value };
                              setLambs(next);
                            }}
                          >
                            <option value="">Nieznany</option>
                            {fatherSelectOptions().map((ram) => (
                              <option key={ram.tag} value={ram.tag}>
                                {formatTag(ram.tag)}
                                {ram.color ? ` · ${sireColorLabel(ram.color)}` : ""}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            </div>

            <div className="lambing-event-footer">
              {!skipPassword ? (
                <Field label="Hasło administratora">
                  <input
                    className="input"
                    type="password"
                    data-testid="lambing-password"
                    value={eventForm.password}
                    onChange={(event) =>
                      setEventForm({ ...eventForm, password: event.target.value })
                    }
                    {...passwordFieldProps(`${fieldNs}-event-password`)}
                  />
                </Field>
              ) : null}
              <div className="modal__actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={closeTool}
                  disabled={busy}
                >
                  Anuluj
                </button>
                <button className="btn" type="submit" disabled={busy} data-testid="lambing-save">
                  {editingEventId != null ? "Zapisz zmiany" : "Zapisz wykot"}
                </button>
              </div>
            </div>
          </>
        ) : null}
      </FormModal>

      <FormModal
        open={notesLamb != null}
        onClose={closeNotes}
        title={notesLamb ? `Notatki — ${formatTag(notesLamb.working_tag)}` : "Notatki"}
        testId="lambing-notes-modal"
        onSubmit={onAddNote}
      >
        {Array.isArray(notesLamb?.notes) && notesLamb.notes.length > 0 ? (
          <ul className="list">
            {notesLamb.notes.map((note) => (
              <li key={String(note.id)} className="list__item">
                <div>
                  <p>{note.text}</p>
                  {note.created_at ? (
                    <p className="muted">{formatDateTime(note.created_at)}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Brak notatek dla tego jagnięcia.</p>
        )}
        <Field label="Nowa notatka">
          <textarea
            className="input"
            rows={3}
            value={noteText}
            onChange={(event) => setNoteText(event.target.value)}
            required
          />
        </Field>
        {!skipPassword ? (
          <Field label="Hasło administratora">
            <input
              className="input"
              type="password"
              value={notePassword}
              onChange={(event) => setNotePassword(event.target.value)}
              {...passwordFieldProps(`${fieldNs}-note-password`)}
            />
          </Field>
        ) : null}
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={closeNotes} disabled={busy}>
            Zamknij
          </button>
          <button className="btn" type="submit" disabled={busy}>
            Dodaj notatkę
          </button>
        </div>
      </FormModal>

      <FormModal
        open={weightsLamb != null}
        onClose={closeWeights}
        title={weightsLamb ? `Ważenie — ${formatTag(weightsLamb.working_tag)}` : "Ważenie"}
        testId="lambing-weights-modal"
        onSubmit={onSaveWeights}
      >
        {weightsLamb ? (
          <p className="muted">
            Nr roboczy:{" "}
            <strong>
              <EarTag value={weightsLamb.working_tag} />
            </strong>
            {weightsLamb.official_tag ? (
              <>
                {" "}
                · oficjalny: <EarTag value={weightsLamb.official_tag} />
              </>
            ) : null}
            {weightsLamb.mother_tag ? (
              <>
                {" "}
                · matka: <EarTag value={weightsLamb.mother_tag} />
              </>
            ) : null}
          </p>
        ) : null}
        <div className="form__row">
          {weightsValues.map((value, index) => (
            <Field key={`weight-field-${index + 1}`} label={`Waga ${index + 1} (kg)`}>
              <input
                className="input"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={value}
                data-testid={`lambing-weight-${index + 1}`}
                onChange={(event) => {
                  const next = [...weightsValues];
                  next[index] = event.target.value;
                  setWeightsValues(next);
                }}
              />
            </Field>
          ))}
        </div>
        {!skipPassword ? (
          <Field label="Hasło administratora">
            <input
              className="input"
              type="password"
              value={weightsPassword}
              onChange={(event) => setWeightsPassword(event.target.value)}
              data-testid="lambing-weights-password"
              {...passwordFieldProps(`${fieldNs}-weights-password`)}
            />
          </Field>
        ) : null}
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={closeWeights} disabled={busy}>
            Anuluj
          </button>
          <button className="btn" type="submit" disabled={busy} data-testid="lambing-weights-save">
            Zapisz wagi
          </button>
        </div>
      </FormModal>

      <FormModal
        open={deleteTarget != null}
        onClose={closeDeleteModal}
        title={
          deleteTarget?.kind === "event"
            ? "Usunąć wykot matki?"
            : "Usunąć jagnię?"
        }
        testId="lambing-delete-modal"
        onSubmit={(event) => {
          event.preventDefault();
          void confirmDelete();
        }}
      >
        {deleteTarget?.kind === "event" ? (
          <p className="muted">
            Usuniesz cały wykot matki <strong><EarTag value={deleteTarget.motherTag} /></strong> wraz ze wszystkimi
            jagniętami z tego wpisu. Tej operacji nie da się cofnąć.
          </p>
        ) : deleteTarget?.kind === "lamb" ? (
          <p className="muted">
            Usuniesz tylko jagnię <strong><EarTag value={deleteTarget.lamb.working_tag} /></strong>
            {deleteTarget.lamb.mother_tag ? (
              <>
                {" "}
                (matka <strong><EarTag value={deleteTarget.lamb.mother_tag} /></strong>)
              </>
            ) : null}
            . Pozostałe jagnięta z tego wykotu zostaną.
          </p>
        ) : null}
        {!skipPassword ? (
          <Field label="Hasło administratora">
            <input
              className="input"
              type="password"
              value={deletePassword}
              onChange={(event) => setDeletePassword(event.target.value)}
              data-testid="lambing-delete-password"
              {...passwordFieldProps(`${fieldNs}-delete-password`)}
            />
          </Field>
        ) : null}
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={closeDeleteModal} disabled={busy}>
            Anuluj
          </button>
          <button
            className="btn btn--danger"
            type="submit"
            disabled={busy}
            data-testid="lambing-delete-confirm"
          >
            Usuń
          </button>
        </div>
      </FormModal>

      <FormModal
        open={irzTarget != null}
        onClose={() => {
          setIrzTarget(null);
          setIrzPassword("");
        }}
        title={irzTarget?.register ? "Rejestracja IRZ" : "Cofnij status IRZ"}
        testId="lambing-irz-modal"
        onSubmit={(event) => {
          event.preventDefault();
          void confirmIrz();
        }}
      >
        {irzTarget?.register ? (
          <p className="muted">
            Oznaczysz jagnię <strong><EarTag value={irzTarget.lamb.working_tag} /></strong> jako zarejestrowane w
            IRZ (kolczyk <strong><EarTag value={irzTarget.lamb.official_tag} /></strong>). Numer zostanie trwale
            usunięty z puli wolnych kolczyków i nie wróci nawet po cofnięciu statusu.
          </p>
        ) : irzTarget ? (
          <p className="muted">
            Cofniesz status IRZ dla jagnięcia <strong><EarTag value={irzTarget.lamb.working_tag} /></strong>. Numer
            oficjalny nie wraca do puli wolnych kolczyków.
          </p>
        ) : null}
        {!skipPassword ? (
          <Field label="Hasło administratora">
            <input
              className="input"
              type="password"
              value={irzPassword}
              onChange={(event) => setIrzPassword(event.target.value)}
              data-testid="lambing-irz-password"
              {...passwordFieldProps(`${fieldNs}-irz-password`)}
            />
          </Field>
        ) : null}
        <div className="modal__actions">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setIrzTarget(null);
              setIrzPassword("");
            }}
            disabled={busy}
          >
            Anuluj
          </button>
          <button className="btn" type="submit" disabled={busy} data-testid="lambing-irz-confirm">
            {irzTarget?.register ? "Zarejestruj w IRZ" : "Cofnij status"}
          </button>
        </div>
      </FormModal>
    </main>
  );
}
